// RB-MY01-PalmGarden.web/src/components/PlantForm/index.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, Plant, PlantFormData } from '../../services/api';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  SelectChangeEvent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Import form sections
import BasicInfo from './components/FormSections/BasicInfo';
import Classification from './components/FormSections/Classification';
import Description from './components/FormSections/Description';
import Landscaping from './components/FormSections/Landscaping';
import PlantCare from './components/FormSections/PlantCare';
import FoliarDetails from './components/FormSections/FoliarDetails';
import FloralDetails from './components/FormSections/FloralDetails';
import ImagesPublish from './components/FormSections/ImagesPublish';

// Import steps from constants
import { steps } from './constants';

// Define section themes
const sectionThemes = {
  basicInfo: {
    color: '#4caf50',
    icon: <i className="fas fa-info-circle" style={{ marginRight: '8px' }} />
  },
  classification: {
    color: '#2196f3',
    icon: <i className="fas fa-sitemap" style={{ marginRight: '8px' }} />
  },
  description: {
    color: '#ff9800',
    icon: <i className="fas fa-align-left" style={{ marginRight: '8px' }} />
  },
  // Add other themes as needed
};

// Define the fadeIn animation
const fadeIn = {
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  animation: 'fadeIn 0.3s ease-out'
};

const PlantForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const isEditMode = Boolean(id);
  
  const [activeStep, setActiveStep] = useState(0);
  const [plant, setPlant] = useState<Partial<Plant>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Load plant data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchPlant = async () => {
        try {
          const plantData = await api.getPlantById(parseInt(id));
          setPlant(plantData);
          if (plantData.imageUrl) {
            setPreviewUrl(plantData.imageUrl);
          }
        } catch (err) {
          console.error('Error fetching plant:', err);
          setError('Failed to load plant data. Please try again.');
        }
      };
      
      fetchPlant();
    }
  }, [isEditMode, id, api]);
  
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPlant(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle select changes for string values
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setPlant(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle select changes for number values
  const handleNumberSelectChange = (event: SelectChangeEvent<number>) => {
    const { name, value } = event.target;
    setPlant(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle multi-select changes
  const handleMultiSelectChange = (event: SelectChangeEvent<string[]>) => {
    const { name, value } = event.target;
    setPlant(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle switch changes
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPlant(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  // Handle image changes
  const handleImageChange = (file: File | null) => {
    setImageFile(file);
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(plant.imageUrl || null);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Create a PlantFormData object from the plant object
      const plantData: PlantFormData = {
        name: plant.name || '',
        type: plant.type || '',
        description: plant.description || '',
        careInstructions: plant.careInstructions || '',
        wateringFrequency: plant.wateringFrequency || 7,
        lightRequirement: plant.lightRequirement || '',
        imageUrl: plant.imageUrl || '',
        published: plant.published || false,
        plantId: plant.plantId || `PG-${Date.now().toString(36).toUpperCase()}`,
        // Add all other properties from plant
        ...plant
      };
      
      // Log the data being sent to help debug
      console.log('Submitting plant data:', plantData);
      
      let result;
      if (isEditMode && id) {
        result = await api.updatePlant(parseInt(id), plantData);
      } else {
        result = await api.createPlant(plantData);
      }
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/plants');
        }, 2000);
      } else {
        // Show the specific error message from the API
        setError(result.message || 'Failed to save plant. Please try again.');
        console.error('API error:', result);
      }
    } catch (err) {
      // Log the full error for debugging
      console.error('Error saving plant:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle next step
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Get content for current step
  const getStepContent = (step: number) => {
    const commonProps = {
      plant,
      handleChange,
      handleSelectChange,
      handleNumberSelectChange,
      handleMultiSelectChange,
      handleSwitchChange,
      handleImageChange,
      previewUrl
    };
    
    switch (step) {
      case 0:
        return <BasicInfo {...commonProps} />;
      case 1:
        return <Classification {...commonProps} />;
      case 2:
        return <Description {...commonProps} />;
      case 3:
        return <Landscaping {...commonProps} />;
      case 4:
        return <PlantCare {...commonProps} />;
      case 5:
        return <FoliarDetails {...commonProps} />;
      case 6:
        return <FloralDetails {...commonProps} />;
      case 7:
        return <ImagesPublish {...commonProps} />;
      default:
        return <div>Unknown step</div>;
    }
  };
  
  // Calculate form completion progress
  const calculateProgress = () => {
    const requiredFields = [
      'name',
      'type',
      'description',
      'careInstructions',
      'wateringFrequency',
      'lightRequirement'
    ];
    
    const filledFields = requiredFields.filter(field => {
      const value = plant[field as keyof typeof plant];
      return Boolean(value && (Array.isArray(value) ? value.length > 0 : true));
    });
    
    return Math.round((filledFields.length / requiredFields.length) * 100);
  };
  
  // Check if current step is valid
  const isCurrentStepValid = () => {
    switch (activeStep) {
      case 0: // Basic Information
        return Boolean(plant.name && plant.type);
      case 1: // Classification
        return true; // Optional fields
      case 2: // Description
        return Boolean(plant.description);
      case 3: // Landscaping
        return true; // Optional fields
      case 4: // Plant Care
        return Boolean(plant.careInstructions && plant.wateringFrequency && plant.lightRequirement);
      case 5: // Foliar Details
        return true; // Optional fields
      case 6: // Floral Details
        return true; // Optional fields
      case 7: // Images & Publish
        return true; // Optional fields
      default:
        return false;
    }
  };
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/plants')}
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2, borderRadius: 1 }}
          >
            Back to Plants
          </Button>
          <Typography variant="h4" fontWeight="600" color="primary.main">
            {isEditMode ? 'Edit Plant' : 'Add New Plant'}
          </Typography>
        </Box>
        
        {/* Improved progress indicator */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={calculateProgress()} 
              sx={{ 
                height: 8, 
                borderRadius: 2,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2
                }
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {`${calculateProgress()}% Complete`}
            </Typography>
          </Box>
        </Box>
        
        {/* Hide the stepper on small screens and show a simpler step indicator */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: 5,
              py: 2,
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'success.main', 
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: 'text.primary',
                fontWeight: 500,
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'primary.main',
              },
              '& .MuiStepLabel-label.Mui-active': {
                fontWeight: 600,
              }
            }}
            alternativeLabel
          >
            {steps.map((label, index) => (
              <Step key={label} completed={activeStep > index}>
                <StepLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {label}
                    {index === activeStep && (
                      <LinearProgress 
                        sx={{ 
                          width: '80%', 
                          mt: 1, 
                          height: 4, 
                          borderRadius: 2,
                          backgroundColor: 'rgba(0,0,0,0.1)'
                        }} 
                      />
                    )}
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        
        {/* Alert messages with improved styling */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 4, borderRadius: 1 }}
            variant="filled"
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 4, borderRadius: 1 }}
            variant="filled"
          >
            Plant {isEditMode ? 'updated' : 'created'} successfully!
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          {getStepContent(activeStep)}
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ 
                borderRadius: 1,
                px: 3,
                py: 1,
                fontWeight: 500
              }}
            >
              Back
            </Button>
            
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading || !isCurrentStepValid()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{ 
                    minWidth: 180, 
                    borderRadius: 1,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: 2
                  }}
                >
                  {isEditMode ? 'Update Plant' : 'Create Plant'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid()}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    minWidth: 120, 
                    borderRadius: 1,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: 2
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>
        
        {/* Floating summary - commented out until implemented */}
        {/* <FloatingSummary plant={plant} /> */}
      </Paper>
    </Box>
  );
};

export default PlantForm;
